import {
    CGFSystemCodes,
    CGS200Code,
    OverdoorFillerCode,
    OverdoorNoneCode,
    OverdoorTransomCode,
} from "@/app/constants";
import { ReactiveComponent } from "./base-component";
import { OverdoorMaterial } from "./overdoor-material";
import { ReactiveComponentDimensions } from "./dimensions";
import { Partition } from "./partition";
import { Door } from "./door";

import {
    IComponentConfig,
    IStructureComponent,
} from "@/app/@types/base-component";
import { IDimensions } from "@/app/@types/dimensions";
import { unwrapRef } from "@/utils/reactive";

export class Overdoor extends ReactiveComponent {
    constructor(config: IComponentConfig) {
        super({ ...config, type: "overdoor" });
    }

    replaceHardwares = (components: IStructureComponent[]) => {
        this.replaceComponents("overdoor-hardware", components);
    };

    resetDimensions = () => {
        this.update({ dimensions: new ReactiveComponentDimensions() });
    };

    setDimensionsFromPartition = (partition: Partition) => {
        if (
            !this.dimensions.height.isEmpty() &&
            !this.dimensions.width.isEmpty()
        ) {
            return;
        }

        const widthFromPartition = partition.dimensions.width.copy();
        const heightFromPartition = partition.dimensions.height.copy();

        widthFromPartition.update({ ft: 0 });
        heightFromPartition.update({ ft: 0 });

        this.dimensions.width.update(widthFromPartition.getValues());
        this.dimensions.height.update(heightFromPartition.getValues());
    };

    setTransomDimensions = (partition: Partition, door: Door) => {
        const remainingHeight = this.caclulateRemainingOverdoorHeight(
            partition,
            door,
        );

        this.dimensions.width.update(door.dimensions.width.getValues());
        this.dimensions.height.update(remainingHeight.getValues());
    };

    checkTransomHeight = (partition: Partition, door: Door) => {
        const remainingHeight = this.caclulateRemainingOverdoorHeight(
            partition,
            door,
        );

        return remainingHeight;
    };

    replaceMaterial = (materials: OverdoorMaterial[]): void => {
        this.replaceComponents("overdoor-material", materials);
    };

    isNoneOverdoor = (): boolean => {
        return this.structureType?.value === OverdoorNoneCode;
    };

    isGlassOverdoor = (): boolean => {
        const materials = this.getMaterials();
        return materials.every((material) => material.isGlassMaterial());
    };

    isAluminiumOverdoor = (): boolean => {
        const materials = this.getMaterials();
        return materials.every((material) => material.isAlumiumMaterial());
    };

    isEditable = (): boolean => {
        return this.structureType?.value !== OverdoorNoneCode;
    };

    isFiller = (): boolean => {
        return this.structureType?.value === OverdoorFillerCode;
    };

    isTransom = (): boolean => {
        return this.structureType?.value === OverdoorTransomCode;
    };

    getGlass = () => {
        return this.components.find(
            (component) => component.type === "glassPartition",
        );
    };

    getGlassThinkness = () => {
        return this?.components.find(
            (component) => component.type === "glassThinknessPartition",
        );
    };

    getMaterial = () => {
        return this.components.filter(
            (component) => component.type === "overdoor-material",
        );
    };

    getOutOfSquare = () => {
        return this.components.find(
            (component) => component.type === "outOfSquare",
        );
    };

    getAsset = () => {
        return this.components.find(
            (component) => component.type === "overdoor-asset",
        );
    };

    hasOverdoorAssets = (installedSystemCode: string): boolean => {
        return [CGFSystemCodes, CGS200Code].includes(installedSystemCode);
    };

    getActiveGlass = () => {
        return this.components.find(
            (component) => component.type === "glassPartition",
        );
    };

    getActiveGlassThinkness = () => {
        const glass = this.getActiveGlass();

        return glass?.components.find(
            (component) => component.type === "glassThinknessPartition",
        );
    };

    private getMaterials = (): OverdoorMaterial[] => {
        const materials: unknown[] =
            this.getComponentsByType("overdoor-material");

        return materials as OverdoorMaterial[];
    };

    private caclulateRemainingOverdoorHeight = (
        partition: Partition,
        door: Door,
    ): IDimensions => {
        return partition.dimensions.height.substract(
            unwrapRef(door.dimensions.height),
        );
    };
}
